<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useBankId, type BankIdMode, type BankIdOptions } from './useBankId.js'
import { computed, watch } from 'vue'
import mdiClose from '~icons/mdi/close'
import mdiCheck from '~icons/mdi/check'
import { useDisplay } from 'vuetify'

const props = defineProps<{
  modelValue?: boolean
  mode: BankIdMode
  signingData?: unknown
  signingVisibleData?: string
  initEndpoint?: string
  pollEndpoint?: string
  cancelEndpoint?: string
  createSigningTokenEndpoint?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'completed': []
}>()

const { t } = useI18n({ inheritLocale: true })
const display = useDisplay()

const bankIdOptions = computed<BankIdOptions>(() => ({
  signing: {
    data: props.signingData,
    message: props.signingVisibleData,
  },
  endpointOverrides: {
    init: props.initEndpoint,
    poll: props.pollEndpoint,
    cancel: props.cancelEndpoint,
    createSigningToken: props.createSigningTokenEndpoint,
  },
}))

const bankId = useBankId(props.mode, bankIdOptions)

const qrDataUrl = computed(() =>
  bankId.qrBase64.value != null ? `data:image/svg+xml;base64,${bankId.qrBase64.value}` : undefined
)
const autoStartUrl = computed(() => {
  if (bankId.autostartToken.value == null) return undefined

  const url = new URL('https://app.bankid.com')
  url.searchParams.append('autostarttoken', bankId.autostartToken.value)
  url.searchParams.append('redirect', window.location.href)
  return url.href
})

const hintCodeMessages: Record<string, string> = {
  userCancel: t(`common.${props.mode}_was_canceled`),
  startFailed: t(`common.${props.mode}_took_to_long`),
  expiredTransaction: t(`common.${props.mode}_took_to_long`),
}

watch(
  () => props.modelValue,
  (isVisible) => {
    if (isVisible) {
      void bankId.start()
    } else {
      bankId.reset()
    }
  }
)

watch(bankId.status, (newStatus) => {
  if (newStatus === 'completed') {
    emit('completed')
  }
})

async function openBankIdDialog() {
  if (!bankId.autostartToken.value) {
    await bankId.start()
  }

  bankId.prepareForRedirect()
  if (autoStartUrl.value) {
    // from MobileBankID developer documentation
    const link = document.createElement('a')
    link.href = autoStartUrl.value
    link.click()
  }
}

function onDialogUpdate(isVisible: boolean) {
  emit('update:modelValue', isVisible)
}
</script>

<template>
  <v-dialog
    max-width="550"
    :model-value="props.modelValue"
    @update:model-value="onDialogUpdate($event)"
  >
    <template #activator="bind">
      <slot
        name="activator"
        v-bind="bind"
      />
    </template>

    <template #default>
      <v-card>
        <v-card-item>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>
              {{ t('common.mobile_bankid') }}
            </span>
            <v-btn
              variant="text"
              size="small"
              :icon="mdiClose"
              :aria-label="t('common.cancel')"
              @click="emit('update:modelValue', false)"
            />
          </v-card-title>
        </v-card-item>

        <v-card-text>
          <v-alert
            v-if="bankId.error.value != null"
            type="error"
            density="compact"
          >
            {{
              bankId.error.value._locale_message != null
                ? t(bankId.error.value._locale_message)
                : (bankId.error.value._error?.message ?? t('system.error'))
            }}
          </v-alert>

          <v-alert
            v-else-if="bankId.status.value === 'failed'"
            type="warning"
            density="compact"
          >
            {{ hintCodeMessages[bankId.hintCode.value!] ?? t('system.error') }}
            <a
              href="#"
              style="color: inherit"
              @click.prevent="bankId.start()"
              >{{ t('common.try_again') }}</a
            >
          </v-alert>

          <v-card
            v-else
            height="200"
            width="200"
            class="mb-4 d-flex flex-column justify-center align-center mx-auto"
          >
            <template v-if="bankId.status.value === 'completed'">
              <m
                :icon="mdiCheck"
                class="mb-4"
                color="success"
                size="3rem"
              />
              <span>{{ t(`common.${mode}_successful`) }}</span>
            </template>

            <v-img
              v-else-if="qrDataUrl && bankId.hintCode.value !== 'userSign'"
              :src="qrDataUrl"
              width="100%"
              aspect-ratio="1/1"
              alt=""
            />

            <template v-else>
              <v-progress-circular
                class="mb-4"
                color="primary"
                size="3rem"
                indeterminate
              />
              <span v-if="bankId.hintCode.value === 'userSign'">{{ t(`common.${mode}_in_progress`) }}</span>
            </template>
          </v-card>
        </v-card-text>

        <v-card-text class="text-center d-flex flex-column align-center ga-2">
          <span>
            {{ t(`common.bankid_${mode}_qr_information`) }}
          </span>

          <span>
            {{ t('common.or') }}
          </span>

          <v-btn
            @click="openBankIdDialog"
            :disabled="bankId.status.value === 'completed' || bankId.hintCode.value === 'userSign'"
            :class="{
              'mb-6': true,
              'full-width': display.smAndDown.value,
            }"
            variant="outlined"
          >
            {{ t('common.open_bankid_on_the_same_device') }}
          </v-btn>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="emit('update:modelValue', false)"
            variant="text"
            color="secondary"
          >
            {{ t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>
